import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Film, Loader2, Search, Star, Trash2, Download, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { imageStudioService } from '../../services/imageStudioService';
import { videoStudioService } from '../../services/videoStudioService';
import { GeneratedImage } from '../../types/imageStudio';
import { GeneratedVideo } from '../../types/videoStudio';
import ImageGallery from '../image-studio/ImageGallery';

type MediaTab = 'images' | 'videos';

interface Props {
  campaignId: string;
}

const CampaignMediaGallery: React.FC<Props> = ({ campaignId }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<MediaTab>('images');
  const [imageCount, setImageCount] = useState<number | null>(null);
  const [videoCount, setVideoCount] = useState<number | null>(null);

  // Probe counts so the toggle shows badges (helps the user see at a glance which type has assets)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [imgs, vids] = await Promise.all([
          imageStudioService.getImages({ campaign_id: campaignId, page: 1, limit: 1 } as any).catch(() => null),
          videoStudioService.listVideos({ campaign_id: campaignId, limit: 1 } as any).catch(() => null)
        ]);
        if (cancelled) return;
        const ic = (imgs as any)?.pagination?.total ?? (Array.isArray((imgs as any)?.data) ? (imgs as any).data.length : 0);
        const vc = (vids as any)?.pagination?.total ?? (Array.isArray((vids as any)?.data) ? (vids as any).data.length : 0);
        setImageCount(ic);
        setVideoCount(vc);
      } catch { /* counts are nice-to-have */ }
    };
    load();
    return () => { cancelled = true; };
  }, [campaignId]);

  return (
    <div className="space-y-4">
      {/* Type toggle */}
      <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
        <button
          onClick={() => setTab('images')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
            tab === 'images'
              ? 'bg-primary-500 text-white shadow'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
          {t('campaigns.media.images', 'Imagens')}
          {imageCount !== null && (
            <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-mono tabular-nums ${
              tab === 'images' ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}>{imageCount}</span>
          )}
        </button>
        <button
          onClick={() => setTab('videos')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
            tab === 'videos'
              ? 'bg-primary-500 text-white shadow'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
          }`}
        >
          <Film className="w-4 h-4" />
          {t('campaigns.media.videos', 'Vídeos')}
          {videoCount !== null && (
            <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-mono tabular-nums ${
              tab === 'videos' ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}>{videoCount}</span>
          )}
        </button>
      </div>

      {/* Content */}
      {tab === 'images' ? (
        <ImageGallery campaignId={campaignId} />
      ) : (
        <CampaignVideoGallery campaignId={campaignId} />
      )}
    </div>
  );
};

// Inline video gallery — mirrors ImageGallery's UX but for video assets tied to the campaign.
const CampaignVideoGallery: React.FC<{ campaignId: string }> = ({ campaignId }) => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState<GeneratedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [preview, setPreview] = useState<GeneratedVideo | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await videoStudioService.listVideos({
        campaign_id: campaignId,
        is_favorite: favoritesOnly ? 'true' : undefined,
        search: search || undefined,
        limit: 60
      });
      if (r.success) setVideos((r.data || []).filter(v => v.original_url));
    } catch (e) {
      console.warn('CampaignVideoGallery: load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId, favoritesOnly]);
  useEffect(() => {
    const h = setTimeout(() => load(), 400);
    return () => clearTimeout(h);
    /* eslint-disable-next-line */
  }, [search]);

  const handleToggleFavorite = async (e: React.MouseEvent, v: GeneratedVideo) => {
    e.stopPropagation();
    try {
      await videoStudioService.update(v.id, { is_favorite: !v.is_favorite });
      setVideos(prev => prev.map(x => x.id === v.id ? { ...x, is_favorite: !x.is_favorite } : x));
    } catch {
      toast.error(t('errors.server_error', 'Error') as string);
    }
  };

  const handleDelete = async (e: React.MouseEvent, v: GeneratedVideo) => {
    e.stopPropagation();
    if (!window.confirm(t('videoStudio.gallery.deleteConfirm', 'Excluir esse vídeo?') as string)) return;
    try {
      await videoStudioService.remove(v.id);
      setVideos(prev => prev.filter(x => x.id !== v.id));
      toast.success(t('videoStudio.gallery.deleted', 'Vídeo excluído') as string);
    } catch {
      toast.error(t('errors.server_error', 'Error') as string);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('videoStudio.gallery.searchPlaceholder', 'Buscar vídeos...') as string}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button
          onClick={() => setFavoritesOnly(f => !f)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm transition-colors ${
            favoritesOnly
              ? 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          <Star className={`w-4 h-4 ${favoritesOnly ? 'fill-yellow-400' : ''}`} />
          {t('imageStudio.favorites', 'Favoritos')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Film className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">{t('videoStudio.gallery.empty', 'Nenhum vídeo nessa campanha ainda') as string}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
          {videos.map(v => {
            const title = v.title || v.prompt?.slice(0, 40) || t('videoStudio.media.clip', 'Vídeo');
            return (
              <div
                key={v.id}
                onClick={() => setPreview(v)}
                className="group relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer transition-all hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600"
              >
                <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt="" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <video src={v.original_url || undefined} preload="metadata" muted className="max-w-full max-h-full object-contain" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition">
                    <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition drop-shadow-lg" />
                  </div>
                  {v.duration_seconds ? (
                    <span className="absolute bottom-1 right-1 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-white font-mono tabular-nums">
                      {Math.round(v.duration_seconds)}s
                    </span>
                  ) : null}
                </div>

                {/* Hover actions */}
                <div className="absolute inset-0 z-10 flex items-end justify-end p-2 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1.5">
                    {v.original_url && (
                      <a
                        href={v.original_url}
                        target="_blank" rel="noopener noreferrer"
                        download
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                        title={t('imageStudio.downloadTooltip', 'Baixar') as string}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={(e) => handleDelete(e, v)}
                      className="p-1.5 rounded bg-red-500 text-white hover:bg-red-600 shadow-sm"
                      title={t('imageStudio.delete', 'Excluir') as string}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Favorite */}
                <button
                  onClick={(e) => handleToggleFavorite(e, v)}
                  className="absolute top-2 right-2 z-20 p-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 transition-colors shadow-sm"
                  title={v.is_favorite ? t('imageStudio.unfavorite', 'Remover dos favoritos') as string : t('imageStudio.favorite', 'Favoritar') as string}
                >
                  <Star className={`w-4 h-4 ${v.is_favorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600 dark:text-gray-300'}`} />
                </button>

                {/* Info */}
                <div className="p-2">
                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(v.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview modal */}
      {preview && preview.original_url && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {preview.title || t('videoStudio.media.clip', 'Vídeo')}
              </h3>
              <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-xl leading-none">×</button>
            </div>
            <video src={preview.original_url} controls autoPlay className="w-full max-h-[70vh] bg-black" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignMediaGallery;
